/** UUID v4/v1 strict pattern for PostgREST filter literals. */
export const STRICT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isStrictUuid(value: string): boolean {
  return STRICT_UUID_RE.test(value.trim());
}

/** ISO-8601 UTC timestamps safe to embed in PostgREST `.or()` filters. */
export function isPostgrestSafeIsoTimestamp(value: string): boolean {
  const s = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/.test(s)) return false;
  return Number.isFinite(Date.parse(s));
}

/** Calendar date keys (YYYY-MM-DD) safe for PostgREST date filters. */
export function isPostgrestSafeDateKey(value: string): boolean {
  const s = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function assertPostgrestSafeDateKey(value: string): string {
  const trimmed = value.trim();
  if (!isPostgrestSafeDateKey(trimmed)) {
    throw new Error('invalid_date_key');
  }
  return trimmed;
}

/**
 * Payments with payment_date in range OR null payment_date but created_at in range,
 * excluding sandbox rows — single PostgREST `.or()` (chaining two `.or()` calls fails).
 */
export function buildPaymentDateWindowProductionOrFilter(fromDateKey: string, toDateKey: string): string {
  const from = assertPostgrestSafeDateKey(fromDateKey);
  const to = assertPostgrestSafeDateKey(toDateKey);
  const productionOnly = 'or(is_sandbox.is.null,is_sandbox.eq.false)';
  return [
    `and(payment_date.gte.${from},payment_date.lte.${to},${productionOnly})`,
    `and(payment_date.is.null,created_at.gte.${from}T00:00:00,created_at.lte.${to}T23:59:59,${productionOnly})`,
  ].join(',');
}

/** Payments with payment_date in range OR null payment_date but created_at in range. */
export function buildPaymentDateWindowOrFilter(fromDateKey: string, toDateKey: string): string {
  const from = assertPostgrestSafeDateKey(fromDateKey);
  const to = assertPostgrestSafeDateKey(toDateKey);
  return `and(payment_date.gte.${from},payment_date.lte.${to}),and(payment_date.is.null,created_at.gte.${from}T00:00:00,created_at.lte.${to}T23:59:59)`;
}

export function assertPostgrestSafeUuid(value: string, label = 'id'): string {
  const trimmed = value.trim();
  if (!isStrictUuid(trimmed)) {
    throw new Error(`invalid_${label}`);
  }
  return trimmed;
}

export function assertPostgrestSafeIsoTimestamp(value: string): string {
  const trimmed = value.trim();
  if (!isPostgrestSafeIsoTimestamp(trimmed)) {
    throw new Error('invalid_timestamp');
  }
  return trimmed;
}

/** Strip PostgREST filter delimiter chars from free-text ilike terms. */
export function sanitizePostgrestIlikeTerm(raw: string, maxLen = 64): string {
  return raw
    .trim()
    .slice(0, maxLen)
    .replace(/[,.()\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildNameIlikeOrFilter(
  searchTerm: string,
  columns: string[] = ['first_name', 'last_name', 'full_name'],
): string {
  const safe = sanitizePostgrestIlikeTerm(searchTerm);
  if (!safe) return 'id.is.null';
  const pattern = `%${safe}%`;
  return columns.map((col) => `${col}.ilike.${pattern}`).join(',');
}

export function buildTwoColumnIlikeOrFilter(searchTerm: string, columnA: string, columnB: string): string {
  const safe = sanitizePostgrestIlikeTerm(searchTerm);
  if (!safe) return 'id.is.null';
  const pattern = `%${safe}%`;
  return `${columnA}.ilike.${pattern},${columnB}.ilike.${pattern}`;
}

export function sanitizePhoneDigits(raw: string, maxLen = 15): string {
  return raw.replace(/\D/g, '').slice(0, maxLen);
}

export function assertPostgrestSafePhoneLiteral(raw: string): string {
  const digits = sanitizePhoneDigits(raw);
  if (digits.length < 6) throw new Error('invalid_phone');
  return digits;
}

export function assertPostgrestSafeEmail(email: string): string {
  const normalized = email.trim().toLowerCase();
  if (!/^[^\s@,().]+@[^\s@,().]+\.[^\s@,().]+$/.test(normalized) || normalized.length > 254) {
    throw new Error('invalid_email');
  }
  return normalized;
}

export function buildPhoneEqOrFilter(variants: string[]): string {
  const literals = [
    ...new Set(
      variants
        .map((v) => v.trim())
        .filter(Boolean)
        .map(assertPostgrestSafePhoneLiteral),
    ),
  ];
  if (literals.length === 0) throw new Error('invalid_phone');
  return literals.flatMap((digits) => [`phone.eq.${digits}`, `phone.eq.+${digits}`]).join(',');
}

export function buildPhoneLookupOrFilter(normalizedPhone: string): string {
  const digits = assertPostgrestSafePhoneLiteral(normalizedPhone);
  const trimmed = normalizedPhone.trim();
  if (!/^\+[\d]+$/.test(trimmed)) {
    return buildPhoneEqOrFilter([digits]);
  }
  return [`phone.eq.${trimmed}`, `phone.eq.${digits}`, `phone.eq.+${digits}`].join(',');
}

export function buildPhoneSuffixIlikeOrFilter(normalizedPhone: string): string {
  const digits = assertPostgrestSafePhoneLiteral(normalizedPhone);
  return `${buildPhoneLookupOrFilter(normalizedPhone)},phone.like.%${digits}%`;
}

export function buildEmailPhoneEqOrFilter(email: string, phone: string): string {
  const safeEmail = assertPostgrestSafeEmail(email);
  return `email.eq.${safeEmail},${buildPhoneLookupOrFilter(phone)}`;
}

export function buildProfileIdPhoneOrFilter(userId: string, normalizedPhone: string): string {
  const id = assertPostgrestSafeUuid(userId, 'user_id');
  return `id.eq.${id},${buildPhoneLookupOrFilter(normalizedPhone)}`;
}

/**
 * Bookings a team owns directly, plus any listed explicitly. Every id is asserted as a
 * strict UUID, so no value can carry a comma or paren that would reshape the filter.
 */
export function buildTeamOrBookingIdsOrFilter(teamId: string, bookingIds: string[]): string {
  const team = assertPostgrestSafeUuid(teamId, 'team_id');
  const ids = [...new Set(bookingIds.map((id) => assertPostgrestSafeUuid(id, 'booking_id')))];
  if (ids.length === 0) {
    return `assigned_team_id.eq.${team}`;
  }
  return `assigned_team_id.eq.${team},id.in.(${ids.join(',')})`;
}

/** Crew-owned jobs, extra assigned ids, and still-unassigned work for that day's dispatch. */
export function buildCrewOrUnassignedOrBookingIdsOrFilter(teamId: string, bookingIds: string[]): string {
  return `${buildTeamOrBookingIdsOrFilter(teamId, bookingIds)},assigned_team_id.is.null`;
}

export function buildCalendarOverlapOrFilter(startIso: string, endIso: string): string {
  const start = assertPostgrestSafeIsoTimestamp(startIso);
  const end = assertPostgrestSafeIsoTimestamp(endIso);
  return `start_time.lte.${end},end_time.gte.${start}`;
}

export function buildPhoneIlikeFilter(digits: string): string {
  const safe = assertPostgrestSafePhoneLiteral(digits);
  return `%${safe}%`;
}

function assertMonthYearPart(value: number, label: 'year' | 'month'): number {
  if (!Number.isInteger(value)) throw new Error(`invalid_${label}`);
  if (label === 'month' && (value < 1 || value > 12)) throw new Error('invalid_month');
  if (label === 'year' && (value < 2000 || value > 2100)) throw new Error('invalid_year');
  return value;
}

export function buildMonthYearBucketsOrFilter(buckets: Array<{ year: number; month: number }>): string {
  return buckets
    .map((bucket) => {
      const year = assertMonthYearPart(bucket.year, 'year');
      const month = assertMonthYearPart(bucket.month, 'month');
      return `and(year.eq.${year},month.eq.${month})`;
    })
    .join(',');
}

export function buildMonthYearRangeOrFilter(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
): string {
  const sy = assertMonthYearPart(startYear, 'year');
  const sm = assertMonthYearPart(startMonth, 'month');
  const ey = assertMonthYearPart(endYear, 'year');
  const em = assertMonthYearPart(endMonth, 'month');
  return `and(year.gt.${sy}),and(year.eq.${sy},month.gte.${sm}),and(year.lt.${ey}),and(year.eq.${ey},month.lte.${em})`;
}

/** Local timestamps without Z (e.g. `2026-06-11T23:59:59.999`). */
export function assertPostgrestSafeLocalTimestamp(value: string): string {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?$/.test(trimmed)) {
    throw new Error('invalid_timestamp');
  }
  // Block PostgREST filter delimiters only — milliseconds dot is allowed by the regex above.
  if (/[,()]/.test(trimmed)) throw new Error('invalid_timestamp');
  return trimmed;
}

export function buildPaymentIsoRangeOrFilter(fromIso: string, toIso: string): string {
  const from = assertPostgrestSafeLocalTimestamp(fromIso);
  const to = assertPostgrestSafeLocalTimestamp(toIso);
  return `and(payment_date.gte.${from},payment_date.lte.${to}),and(payment_date.is.null,created_at.gte.${from},created_at.lte.${to})`;
}

export function buildProfileSearchIlikeOrFilter(searchTerm: string): string {
  const safe = sanitizePostgrestIlikeTerm(searchTerm);
  if (!safe) return 'id.is.null';
  const pattern = `%${safe}%`;
  return `phone.ilike.${pattern},email.ilike.${pattern},first_name.ilike.${pattern},last_name.ilike.${pattern}`;
}

export function buildEmailOptionalPhoneEqOrFilter(email: string, phone?: string | null): string {
  const safeEmail = assertPostgrestSafeEmail(email);
  if (phone?.trim()) {
    return `email.eq.${safeEmail},${buildPhoneLookupOrFilter(phone)}`;
  }
  return `email.eq.${safeEmail}`;
}
